import { IrdEntityMetadataBuilder } from './entity';
import { IrdMetadataLayer } from './layer';
import { IrdPropertyDeclaration, IrdTablePropertyBuilder } from './property-builder';
import { IrdAttributeProvenance, IrdResolvedGroup, IrdResolvedMetadata, IrdResolvedTableMetadata } from './resolved';

/**
 * The three view contexts live here, next to the layer they extend, and **not** in a
 * `scope:shared` partner library of `shared/ng/common`.
 *
 * That is a deliberate deviation from tech design §2.1, whose co-location rule — "the metadata
 * lives where the component that reads it lives" — produced `shared/shared/common` for exactly
 * these three classes. The owner decided against it on 2026-10-05: the library held one source
 * file behind seven of scaffolding, and `shared/shared/base` already carries the same kind of
 * metadata-adjacent machinery in `mapper/`.
 *
 * What the deviation gives up, so the next reader can weigh it rather than rediscover it:
 * §6's second bypass guard. With the contexts in a partner library the schema sat off the
 * components' own dependency path, and reaching past a context for the unfolded schema showed up
 * as a visible import in a review diff. Here it does not. The other two guards still hold — the
 * built models are mutually non-assignable through their `context` literal types, and what a
 * component takes is the built {@link IrdResolvedMetadata}, whose only public members are
 * `context`, `groups`, `entityNameKey` and `explain()`.
 *
 * Why they still do not belong in an `ng` library either: `build()` is the single point where the
 * chain validates, and ADR-0003 wants capability (L0) to bound intent (L3) with a build failure.
 * A context the fold cannot see cannot be checked by it.
 */

/*
 * Each context builder accepts only the entity level or its own context as a parent (ADR-0001,
 * rule 2), so a table's exclusions cannot reach a detail view by construction. The compiler
 * enforces it: every builder redeclares the protected `layerName`, which makes the builders
 * nominal — a table builder is not assignable where a detail builder is expected.
 *
 * A context builder decides the *set*: which properties, their order and groups, whether the table
 * edits inline, the view-wide empty marker. `property(key, (p) => …)` decides one *field*. Recipes:
 * `docs/metadata/cookbook.md`.
 */

/**
 * The detail context: one record, shown as label-and-value pairs.
 *
 * It deliberately does **not** apply the legacy `showInUi` flag. That flag was written with the
 * admin table in mind, so honouring it here is what hid a map's description and index from its
 * own detail page while the table declaration stayed untouched.
 */
export class IrdDetailMetadataBuilder<T> extends IrdMetadataLayer<T, 'detail'> {
  protected readonly layerName = 'detail';

  constructor(parent: IrdEntityMetadataBuilder<T> | IrdDetailMetadataBuilder<T>) {
    super(parent);
  }
}

/**
 * The table context. It does not read the legacy `showInUi` flag: every table declares what it
 * leaves out, on the entity level for a technical identifier and here for its own choice.
 */
export class IrdTableMetadataBuilder<T> extends IrdMetadataLayer<T, 'table'> {
  protected readonly layerName = 'table';
  private ownInlineEditing?: boolean;
  // An empty cell reads quieter in a column than a dash; a detail row without its value looks broken.
  protected override readonly defaultEmptyMarker = '';

  constructor(parent: IrdEntityMetadataBuilder<T> | IrdTableMetadataBuilder<T>) {
    super(parent);
  }

  /**
   * Switches inline editing on, or off, for the whole table. Which cells it reaches is not declared
   * here: every property whose `editable` fact holds. Off unless a table layer of the chain says so;
   * the most derived one that does wins.
   */
  public inlineEditing(enabled = true): this {
    this.ownInlineEditing = enabled;
    return this;
  }

  public override build(): IrdTableMetadata<T> {
    return super.build() as IrdTableMetadata<T>;
  }

  protected override toResolved(
    primaryKey: string & keyof T,
    groups: readonly IrdResolvedGroup<T>[],
    entityNameKey: string | undefined,
    provenance: ReadonlyMap<string, IrdAttributeProvenance>,
  ): IrdTableMetadata<T> {
    return new IrdResolvedTableMetadata<T>(primaryKey, groups, entityNameKey, provenance, this.resolveInlineEditing());
  }

  private resolveInlineEditing(): boolean {
    if (this.ownInlineEditing !== undefined) {
      return this.ownInlineEditing;
    }
    return this.parent instanceof IrdTableMetadataBuilder && this.parent.resolveInlineEditing();
  }

  /** As on every layer, plus what only a table reads: sorting, truncation, width. */
  public override property<K extends string & keyof T>(key: K, declare: IrdPropertyDeclaration<IrdTablePropertyBuilder<T[K]>>): this {
    return this.declareProperty(key, declare, new IrdTablePropertyBuilder<T[K]>());
  }
}

/**
 * The form context. Still reads the legacy `showInUi` flag, so the generic edit dialog offers the
 * fields it offers today until the form has declarations of its own.
 */
export class IrdFormMetadataBuilder<T> extends IrdMetadataLayer<T, 'form'> {
  protected readonly layerName = 'form';

  constructor(parent: IrdEntityMetadataBuilder<T> | IrdFormMetadataBuilder<T>) {
    super(parent);
    this.excludeLegacyHidden();
  }
}

/** What {@link IrdDetailMetadataBuilder} builds, and what `shared-generic-detail-view` takes. */
export type IrdDetailMetadata<T> = IrdResolvedMetadata<T, 'detail'>;

/** What {@link IrdTableMetadataBuilder} builds. */
export type IrdTableMetadata<T> = IrdResolvedTableMetadata<T>;

/** What {@link IrdFormMetadataBuilder} builds. */
export type IrdFormMetadata<T> = IrdResolvedMetadata<T, 'form'>;
